import React, { useState, useEffect } from 'react';
import { LuX, LuMail, LuUserPlus, LuLoader, LuSend, LuCheck, LuCopy, LuCalendarDays, LuChevronDown, LuUsers, LuUser } from 'react-icons/lu';
import * as LucideIcons from 'react-icons/lu';
import { doc, setDoc, collection, addDoc, getDocs, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../../firebaseConfig'; // Adjust path as needed
import { useOrganization } from '../../../context/OrgContext';
import {useUser} from "../../../context/UserContext";
import {useAuth} from "../../../context/AuthContext"; // Adjust path as needed

const InviteModal = ({ isOpen, onClose, moduleName = null }) => {
  const { institution } = useOrganization();
  const [emails, setEmails] = useState(['']);
  const [selectedModule, setSelectedModule] = useState(null); // Now stores full module object
  const [loading, setLoading] = useState(false);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [invitesSent, setInvitesSent] = useState([]);
  const [availableModules, setAvailableModules] = useState([]);
  const [currentUsers, setCurrentUsers] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showUsers, setShowUsers] = useState(true);
  const {currentUser} = useAuth()

  // Function to get Lucide icon component by name
  const getLucideIcon = (iconName) => {
    if (!iconName) return LuUserPlus; // Default fallback icon

    // Handle the icon name format (e.g., "LuCalendarDays" -> LuCalendarDays)
    const IconComponent = LucideIcons[iconName];
    return IconComponent || LuUserPlus; // Fallback to default if icon not found
  };

  // Fetch current users from the organization
  const fetchCurrentUsers = async () => {
    if (!institution?.id) return;

    setUsersLoading(true);
    try {
      const usersRef = collection(db, 'colleges', institution.id, 'users');
      const querySnapshot = await getDocs(usersRef);

      const users = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        users.push({
          id: doc.id, // This is the email
          email: doc.id,
          displayName: data.displayName || '',
          role: data.role || 'Student',
          modules: data.modules || [],
          lastSignIn: data.lastSignIn,
          createdAt: data.createdAt
        });
      });

      // Sort users by displayName, then email
      users.sort((a, b) => {
        const nameA = a.displayName || a.email;
        const nameB = b.displayName || b.email;
        return nameA.localeCompare(nameB);
      });

      setCurrentUsers(users);
    } catch (error) {
      console.error('Error fetching current users:', error);
      setError('Failed to load current users');
    } finally {
      setUsersLoading(false);
    }
  };

  // Fetch modules from Firestore
  const fetchModules = async () => {
    if (!institution?.id) return;

    setModulesLoading(true);
    try {
      const configsRef = collection(db, 'colleges', institution.id, 'configs');
      const querySnapshot = await getDocs(configsRef);

      const modules = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        modules.push({
          id: doc.id,
          moduleId: data.moduleId || doc.id,
          name: data.name,
          description: data.description || 'No description available',
          iconName: data.iconName,
          status: data.status,
          enabledAt: data.enabledAt,
          userRole: data.userRole || 'Student' // Add default role support
        });
      });

      // Filter only configured/enabled modules if needed
      const enabledModules = modules.filter(module =>
        module.status !== 'Not Configured' // Adjust this condition as needed
      );

      const modulesToUse = enabledModules.length > 0 ? enabledModules : modules;
      setAvailableModules(modulesToUse);

      // If moduleName is provided, find and set the matching module
      if (moduleName) {
        const matchingModule = modulesToUse.find(m =>
          m.name === moduleName ||
          m.moduleId === moduleName ||
          m.id === moduleName
        );
        if (matchingModule) {
          setSelectedModule(matchingModule);
        }
      }
    } catch (error) {
      console.error('Error fetching modules:', error);
      setError('Failed to load available modules');
    } finally {
      setModulesLoading(false);
    }
  };

  // Fetch both modules and users when modal opens and institution is available
  useEffect(() => {
    if (isOpen && institution?.id) {
      fetchModules();
      fetchCurrentUsers();
    }
  }, [isOpen, institution?.id]);

  const handleModuleSelect = (module) => {
    setSelectedModule(module); // Store the full module object
    setIsDropdownOpen(false);
  }

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (isDropdownOpen) {
          setIsDropdownOpen(false);
        } else {
          onClose();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isDropdownOpen, onClose]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setEmails(['']);
      if (!moduleName) {
        setSelectedModule(null); // Reset to null when no pre-selected module
      }
      setError('');
      setSuccess('');
      setInvitesSent([]);
      setIsDropdownOpen(false);
      setShowUsers(true);
    }
  }, [isOpen, moduleName]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const addEmailField = () => {
    setEmails([...emails, '']);
  };

  const updateEmail = (index, value) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  const removeEmail = (index) => {
    if (emails.length > 1) {
      const newEmails = emails.filter((_, i) => i !== index);
      setEmails(newEmails);
    }
  };

  const handlePaste = (e, index) => {
    const paste = e.clipboardData.getData('text');
    const emailList = paste.split(/[,;\s\n]+/).filter(email => email.trim());

    if (emailList.length > 1) {
      e.preventDefault();
      const newEmails = [...emails];
      newEmails[index] = emailList[0];
      emailList.slice(1).forEach(email => newEmails.push(email.trim()));
      setEmails(newEmails);
    }
  };

  const generateInviteToken = () => {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  };

  // Function to add or update user in the organization's users collection
  const addOrUpdateUserInOrg = async (email, moduleToInvite) => {
    try {
      const userDocRef = doc(db, 'colleges', institution.id, 'users', email);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        // User exists, add module to their modules array if not already present
        const userData = userDoc.data();
        const currentModules = userData.modules || [];

        if (!currentModules.includes(moduleToInvite.moduleId || moduleToInvite.id)) {
          await updateDoc(userDocRef, {
            modules: arrayUnion(moduleToInvite.moduleId || moduleToInvite.id)
          });
        }
      } else {
        // User doesn't exist, create new user document
        await setDoc(userDocRef, {
          email: email,
          displayName: '',
          role: moduleToInvite.userRole || 'Student',
          modules: [moduleToInvite.moduleId || moduleToInvite.id],
          lastSignIn: '', // empty date as requested
          createdAt: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Error adding/updating user in organization:', error);
      throw error;
    }
  };

  const addUserToModuleConfig = async (email, moduleToInvite) => {
  try {
    const moduleConfigRef = doc(db, 'colleges', institution.id, 'configs', moduleToInvite.moduleId || moduleToInvite.id);

    // Check if the config document exists
    const moduleConfigDoc = await getDoc(moduleConfigRef);

    if (moduleConfigDoc.exists()) {
      // Document exists, add email to users array if not already present
      const configData = moduleConfigDoc.data();
      const currentUsers = configData.users || [];

      if (!currentUsers.includes(email)) {
        await updateDoc(moduleConfigRef, {
          users: arrayUnion(email)
        });
      }
    } else {
      // Document doesn't exist, create it with the user email
      await setDoc(moduleConfigRef, {
        users: [email],
        // Add other default fields as needed
        name: moduleToInvite.name,
        moduleId: moduleToInvite.moduleId || moduleToInvite.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
  } catch (error) {
    console.error('Error adding user to module config:', error);
    throw error;
  }
};

  const sendInvites = async (e) => {
    e.preventDefault();

    const validEmails = emails.filter(email => email.trim() && email.includes('@'));

    if (validEmails.length === 0) {
      setError('Please enter at least one valid email address');
      return;
    }

    // Get module info from selectedModule object or moduleName
    const moduleToInvite = selectedModule || availableModules.find(m =>
      m.name === moduleName || m.moduleId === moduleName || m.id === moduleName
    );

    if (!institution?.id || !moduleToInvite) {
      setError('Missing institution or module information');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const sentInvites = [];

      for (const email of validEmails) {
        // 1. Add or update user in organization's users collection
        await addOrUpdateUserInOrg(email.trim(), moduleToInvite);

        // 2. Add user email to the module config's users array
        await addUserToModuleConfig(email.trim(), moduleToInvite);

        // 3. Create the invite document
        const inviteToken = generateInviteToken();
        const inviteData = {
          email: email.trim(),
          institutionId: institution.id,
          institutionName: institution.fullName,
          moduleName: moduleToInvite.name,
          moduleId: moduleToInvite.moduleId || moduleToInvite.id,
          inviteToken: inviteToken,
          createdAt: new Date(),
          expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
          status: 'pending',
          invitedBy: currentUser.uid,
          role: moduleToInvite.userRole || 'Student',
        };

        // Store invite in Firestore
        const inviteRef = await addDoc(collection(db, 'invites'), inviteData);

        // TODO: Send email with magic link
        const magicLink = `${window.location.origin}/accept-invite/${inviteToken}`;
        console.log(`Send email to ${email} with link: ${magicLink}`);

        sentInvites.push({
          email: email.trim(),
          inviteId: inviteRef.id,
          magicLink: magicLink,
          status: 'sent'
        });
      }

      setInvitesSent(sentInvites);
      setSuccess(`Successfully sent ${sentInvites.length} invite${sentInvites.length > 1 ? 's' : ''}!`);

      // Refresh the current users list to show updated data
      await fetchCurrentUsers();

    } catch (error) {
      console.error('Error sending invites:', error);
      setError('Failed to send invites. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyInviteLink = async (link) => {
    try {
      await navigator.clipboard.writeText(link);
      // You could add a toast notification here
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  // Filter current users based on selected module
  const getUsersForModule = () => {
    if (!selectedModule && !moduleName) return currentUsers;

    const moduleId = selectedModule?.moduleId || selectedModule?.id || moduleName;
    return currentUsers.filter(user =>
      user.modules.includes(moduleId)
    );
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-[#0a0a0a] border border-[#ffffff15] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#ffffff10]">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl">
              <LuUserPlus className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">
                {moduleName ? `Invite to ${moduleName}` : 'Invite Team Members'}
              </h2>
              <p className="text-sm text-gray-400">
                {moduleName
                  ? `Send invites to edit ${moduleName}`
                  : `Add new members to ${institution?.fullName}`
                }
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#ffffff10] rounded-xl transition-all duration-200"
            disabled={loading}
          >
            <LuX className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
          {!invitesSent.length ? (
            <>
              {/* Module Selection Dropdown (only if not pre-selected) */}
              {!moduleName && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-300">
                    Select Module
                  </label>

                  {modulesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <LuLoader className="w-6 h-6 animate-spin text-blue-400" />
                      <span className="ml-2 text-gray-400">Loading modules...</span>
                    </div>
                  ) : (
                    <div className="relative">
                      {availableModules.length > 0 ? (
                        <>
                          {/* Dropdown Button */}
                          <button
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="w-full flex items-center justify-between p-4 bg-[#ffffff08] border border-[#ffffff15] rounded-xl hover:border-[#ffffff25] hover:bg-[#ffffff12] transition-all duration-200"
                          >
                            <div className="flex items-center space-x-3">
                              {selectedModule ? (
                                <>
                                  <div className="p-2 bg-blue-500/20 rounded-lg">
                                    {React.createElement(getLucideIcon(selectedModule.iconName), {
                                      className: "w-4 h-4 text-blue-400"
                                    })}
                                  </div>
                                  <div className="text-left">
                                    <p className="text-white font-medium">{selectedModule.name}</p>
                                    <p className="text-sm text-gray-400">{selectedModule.description}</p>
                                  </div>
                                </>
                              ) : (
                                <div className="flex items-center space-x-3">
                                  <div className="p-2 bg-[#ffffff10] rounded-lg">
                                    <LuUserPlus className="w-4 h-4 text-gray-400" />
                                  </div>
                                  <span className="text-gray-400">Choose a module...</span>
                                </div>
                              )}
                            </div>
                            <LuChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                              isDropdownOpen ? 'rotate-180' : ''
                            }`} />
                          </button>

                          {/* Dropdown Options */}
                          {isDropdownOpen && (
                            <div className="absolute top-full left-0 right-0 mt-2 bg-[#0a0a0a] border border-[#ffffff15] rounded-xl shadow-2xl z-10 max-h-60 overflow-y-auto">
                              {availableModules.map((module) => {
                                const IconComponent = getLucideIcon(module.iconName);
                                const isSelected = selectedModule && (selectedModule.id === module.id);

                                return (
                                  <button
                                    key={module.id}
                                    onClick={() => handleModuleSelect(module)}
                                    className={`w-full flex items-center space-x-3 p-4 hover:bg-[#ffffff08] transition-all duration-200 first:rounded-t-xl last:rounded-b-xl border-b border-[#ffffff08] last:border-b-0 ${
                                      isSelected ? 'bg-blue-500/10' : ''
                                    }`}
                                  >
                                    <div className={`p-2 rounded-lg ${
                                      isSelected ? 'bg-blue-500/20' : 'bg-[#ffffff10]'
                                    }`}>
                                      <IconComponent className={`w-4 h-4 ${
                                        isSelected ? 'text-blue-400' : 'text-gray-400'
                                      }`} />
                                    </div>
                                    <div className="flex-1 text-left">
                                      <h3 className={`font-medium ${
                                        isSelected ? 'text-blue-400' : 'text-white'
                                      }`}>
                                        {module.name}
                                      </h3>
                                      <p className="text-sm text-gray-400">{module.description}</p>
                                    </div>
                                    {isSelected && (
                                      <LuCheck className="w-4 h-4 text-blue-400" />
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-8 text-gray-400">
                          <p>No modules available</p>
                          <button
                            onClick={fetchModules}
                            className="mt-2 text-blue-400 hover:text-blue-300 text-sm"
                          >
                            Retry loading modules
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Current Users Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <LuUsers className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-300">
                      Current Users {(selectedModule || moduleName) &&
                        `in ${selectedModule?.name || moduleName}`}
                    </span>
                  </div>
                  <button
                    onClick={() => setShowUsers(!showUsers)}
                    className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    {showUsers ? 'Hide' : 'Show'}
                  </button>
                </div>

                {showUsers && (
                  <div className="bg-[#ffffff05] border border-[#ffffff10] rounded-xl p-4">
                    {usersLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <LuLoader className="w-4 h-4 animate-spin text-blue-400" />
                        <span className="ml-2 text-gray-400 text-sm">Loading users...</span>
                      </div>
                    ) : (
                      <>
                        {(() => {
                          const usersToShow = getUsersForModule();
                          if (usersToShow.length === 0) {
                            return (
                              <div className="text-center py-4 text-gray-400">
                                <LuUser className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">
                                  {(selectedModule || moduleName)
                                    ? 'No users assigned to this module yet'
                                    : 'No users in organization yet'}
                                </p>
                              </div>
                            );
                          }

                          return (
                            <div className="space-y-2 max-h-32 overflow-y-auto">
                              {usersToShow.map((user) => (
                                <div key={user.id} className="flex items-center justify-between py-2 px-3 bg-[#ffffff05] rounded-lg">
                                  <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full flex items-center justify-center">
                                      <LuUser className="w-4 h-4 text-blue-400" />
                                    </div>
                                    <div>
                                      <p className="text-white text-sm font-medium">
                                        {user.displayName || user.email}
                                      </p>
                                      {user.displayName && (
                                        <p className="text-xs text-gray-400">{user.email}</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-gray-400">{user.role}</p>
                                    <p className="text-xs text-gray-500">
                                      {user.modules.length} module{user.modules.length !== 1 ? 's' : ''}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Email Input Section */}
              <div className="space-y-4">
                <div className={"flex w-full justify-between"}>
                  <label className="block text-sm font-medium text-gray-300">
                    Invite new members
                  </label>
                  <button
                      onClick={addEmailField}
                      className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                      disabled={loading}
                  >
                    + Add another email
                  </button>
                </div>

                {emails.map((email, index) => (
                    <div key={index} className="flex gap-2">
                      <div className="relative flex-1">
                        <LuMail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"/>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => updateEmail(index, e.target.value)}
                            onPaste={(e) => handlePaste(e, index)}
                            placeholder="colleague@company.com"
                            className="w-full pl-10 pr-4 py-3 bg-[#ffffff08] border border-[#ffffff15] rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500/50 focus:bg-[#ffffff12] transition-all duration-200"
                            disabled={loading}
                        />
                      </div>
                      {emails.length > 1 && (
                          <button
                              onClick={() => removeEmail(index)}
                              className="p-3 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all duration-200"
                              disabled={loading}
                          >
                            <LuX className="w-4 h-4"/>
                          </button>
                      )}
                    </div>
                ))}

                <div className="text-xs text-gray-500">
                  💡 Tip: You can paste multiple emails separated by commas
                </div>
              </div>

              {/* Preview */}
              {(moduleName || selectedModule) && (
                  <div className="p-4 bg-[#ffffff05] border border-[#ffffff10] rounded-xl">
                    <div className="text-sm text-gray-300 space-y-1">
                    <p><span className="text-gray-500">Institution:</span> {institution?.fullName}</p>
                    <p><span className="text-gray-500">Module:</span> {
                      moduleName || selectedModule?.name
                    }</p>
                    {(selectedModule?.userRole || moduleName) && (
                      <p><span className="text-gray-500">Role:</span> {
                        selectedModule?.userRole || 'Student'
                      }</p>
                    )}
                    <p><span className="text-gray-500">Expires:</span> 7 days</p>
                  </div>
                </div>
              )}
            </>
          ) : (
            /* Success State */
            <div className="space-y-4">
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <LuCheck className="w-8 h-8 text-green-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">Invites Sent!</h3>
                <p className="text-gray-400">Invites have been sent to the following emails:</p>
              </div>

              <div className="space-y-2">
                {invitesSent.map((invite, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-[#ffffff05] border border-[#ffffff10] rounded-xl">
                    <div>
                      <p className="text-white text-sm font-medium">{invite.email}</p>
                      <p className="text-xs text-green-400">Invite sent</p>
                    </div>
                    <button
                      onClick={() => copyInviteLink(invite.magicLink)}
                      className="p-2 text-gray-400 hover:text-white hover:bg-[#ffffff10] rounded-lg transition-all duration-200"
                      title="Copy invite link"
                    >
                      <LuCopy className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {success && !invitesSent.length && (
            <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
              <p className="text-green-400 text-sm">{success}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-[#ffffff10]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            disabled={loading}
          >
            {invitesSent.length ? 'Done' : 'Cancel'}
          </button>

          {!invitesSent.length && (
            <button
              onClick={sendInvites}
              disabled={loading || emails.every(e => !e.trim()) || (!moduleName && !selectedModule) || modulesLoading}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-600 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-all duration-200 flex items-center space-x-2 shadow-lg hover:shadow-xl"
            >
              {loading ? (
                <>
                  <LuLoader className="w-4 h-4 animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <LuSend className="w-4 h-4" />
                  <span>Send Invites</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InviteModal;